import { z } from 'zod';
/**
 * Configurazione di un Ruleset — il cuore configurabile di PandaCalcio.
 *
 * Questo schema È il contratto tra:
 *  - admin UI (form di configurazione regolamento)
 *  - database (RulesetVersion.config, JSONB)
 *  - scoring engine (input del calcolo)
 *
 * Tutti i punti sono espressi in punti decimali (es. 3.5) con precisione
 * al centesimo; lo scoring engine converte in centesimi interi per il calcolo.
 */
/** Un valore punti: massimo 2 decimali, range sensato per evitare errori di input. */
const points = z.number().multipleOf(0.01).min(-100).max(100);
/** Punteggi 1/X/2 per una combinazione di fasce. */
export const tierCellSchema = z.object({
    home: points, // pronostico 1 corretto
    draw: points, // pronostico X corretto
    away: points, // pronostico 2 corretto
});
/**
 * Matrice fasce: chiave "H-A" dove H = fascia squadra di casa, A = fascia ospite.
 * Fasce: 1 = prime 5, 2 = centrali, 3 = ultime 5 (default, configurabile in `tiers`).
 */
export const tierMatrixSchema = z.object({
    '1-1': tierCellSchema,
    '1-2': tierCellSchema,
    '1-3': tierCellSchema,
    '2-1': tierCellSchema,
    '2-2': tierCellSchema,
    '2-3': tierCellSchema,
    '3-1': tierCellSchema,
    '3-2': tierCellSchema,
    '3-3': tierCellSchema,
});
export const rulesetConfigSchema = z.object({
    /** Struttura della schedina. */
    picks: z.object({
        /** Numero di partite a pronostico fisso (1X2 secco o doppia). */
        fixedCount: z.number().int().min(1).max(20).default(4),
        /** La doppia è consentita? */
        allowDouble: z.boolean().default(true),
        /** La doppia divide il punteggio ottenuto per questo valore. */
        doubleDivisor: z.number().multipleOf(0.01).min(1).max(10).default(2),
    }),
    /** Matrice punteggi base per fasce. */
    tierMatrix: tierMatrixSchema,
    /** Assegnazione automatica delle fasce. */
    tiers: z.object({
        /** Da quale giornata reale parte il calcolo automatico (prima: tutte in fascia 2). */
        autoFromMatchday: z.number().int().min(1).default(5),
        /** Quante squadre in testa vanno in fascia 1. */
        topCount: z.number().int().min(0).default(5),
        /** Quante squadre in coda vanno in fascia 3. */
        bottomCount: z.number().int().min(0).default(5),
    }),
    /** Quinta partita: risultato esatto. */
    exactResult: z.object({
        enabled: z.boolean().default(true),
        /** Punti extra se il risultato esatto è corretto. */
        bonus: points.default(3),
        /**
         * SOLO a risultato esatto indovinato: oltre al bonus si incassano anche
         * i punti base 1X2 del segno implicito. La quinta partita è tutto o
         * niente: esatto sbagliato = zero, anche col segno giusto
         * (decisione utente 2026-10-30).
         */
        givesBasePoints: z.boolean().default(true),
    }),
    /** Bonus 4/4 (tutti i pronostici fissi corretti). */
    bonusAllCorrect: z.object({
        enabled: z.boolean().default(true),
        /** Bonus standard per il 4/4. */
        bonus: points.default(2),
        /** Soglia di punti (dalle sole partite fisse) per il bonus maggiorato. */
        threshold: points.default(8),
        /**
         * Bonus se la soglia è raggiunta. SOSTITUISCE il bonus standard
         * (decisione regolamento: +3 totale, non +5).
         */
        bonusAboveThreshold: points.default(3),
    }),
    /** Malus 0/4 (nessun pronostico fisso corretto). */
    malusNoneCorrect: z.object({
        enabled: z.boolean().default(true),
        penalty: points.default(-1),
    }),
    /** OVER: gol totali della partita scelta. */
    over: z.object({
        enabled: z.boolean().default(true),
        /** Penalità se i gol totali sono sotto il primo livello. */
        penalty: points.default(-2),
        /**
         * Livelli a soglia crescente: si applica il livello più alto raggiunto.
         * Es. [{goals:4,points:3},{goals:6,points:4},{goals:8,points:5}]
         * → 5 gol = +3, 7 gol = +4, 9 gol = +5.
         */
        levels: z
            .array(z.object({ goals: z.number().int().min(0), points }))
            .min(1)
            .default([
            { goals: 4, points: 3 },
            { goals: 6, points: 4 },
            { goals: 8, points: 5 },
        ]),
        /** L'OVER è obbligatorio? Nel regolamento originale è FACOLTATIVO. */
        required: z.boolean().default(false),
    }),
    /** Originalità: premia i pronostici corretti unici, penalizza quelli di massa. */
    originality: z.object({
        enabled: z.boolean().default(true),
        /** Bonus per pronostico corretto che nessun altro ha fatto. */
        uniqueCorrectBonus: points.default(0.5),
        /** Da quanti concorrenti con lo stesso pronostico scatta la penalità. */
        sharedThreshold: z.number().int().min(2).default(3),
        sharedPenalty: points.default(-0.5),
        /** Escludi le doppie dal conteggio. */
        excludeDoubles: z.boolean().default(true),
        /** Escludi i giocatori bot (es. Cita) dal conteggio. */
        excludeBots: z.boolean().default(true),
        /**
         * Escludi le colonne rosse dal CONTEGGIO (ma le colonne rosse
         * beneficiano comunque di bonus/malus originalità sui propri pronostici).
         */
        excludeRedColumns: z.boolean().default(true),
    }),
    /** Colonna rossa: schedina non consegnata entro la deadline. */
    redColumn: z.object({
        /** Riusa la schedina della giornata precedente. */
        usePreviousPrediction: z.boolean().default(true),
        penalty: points.default(-1),
        /** La colonna rossa beneficia dei bonus (4/4, originalità...)? */
        applyBonus: z.boolean().default(true),
        /** La colonna rossa subisce i malus (0/4...)? */
        applyMalus: z.boolean().default(true),
    }),
    /** Capocannoniere: pronostico segreto di inizio campionato. */
    topScorer: z.object({
        enabled: z.boolean().default(true),
        /** Punti totali da dividere tra chi indovina. */
        totalPrize: points.default(6),
        /**
         * In caso di più capocannonieri a pari merito:
         * - anyCounts: indovinarne uno qualsiasi vale
         * - split: il premio si divide tra tutti i vincitori
         */
        tieRule: z.enum(['anyCounts', 'split']).default('anyCounts'),
    }),
    /** Giornata Super: default di lega (la config effettiva è snapshotata sulla giornata). */
    superMatchday: z.object({
        enabled: z.boolean().default(false),
        method: z.enum(['random', 'manual', 'automatic']).default('manual'),
        multiplier: z.number().multipleOf(0.01).min(1).max(10).default(2),
        extraBonus: points.default(0),
        extraPenalty: points.default(0),
    }),
    /** Spareggio di fine campionato in caso di parità. */
    tieBreak: z.object({
        method: z.enum(['playoffMatchday', 'sharedPosition']).default('playoffMatchday'),
    }),
    /** Giocatore bot ("Cita"): pronostici casuali automatici. */
    botPlayer: z.object({
        enabled: z.boolean().default(false),
        name: z.string().min(1).max(50).default('Cita'),
    }),
});
/**
 * Regolamento PandaCalcio originale ("Panda Classic v1"),
 * trascritto da lineeguida.MD §11–§22 + decisioni del 2026-08-25.
 */
export const PANDA_CLASSIC_CONFIG = rulesetConfigSchema.parse({
    picks: { fixedCount: 4, allowDouble: true, doubleDivisor: 2 },
    tierMatrix: {
        // PRIMA - TERZA: 1=1, X=3.5, 2=4
        '1-3': { home: 1, draw: 3.5, away: 4 },
        // TERZA - PRIMA: 1=3, X=3, 2=1
        '3-1': { home: 3, draw: 3, away: 1 },
        // STESSA FASCIA: 1=2, X=3, 2=2.5
        '1-1': { home: 2, draw: 3, away: 2.5 },
        '2-2': { home: 2, draw: 3, away: 2.5 },
        '3-3': { home: 2, draw: 3, away: 2.5 },
        // PRIMA - SECONDA: 1=2, X=3, 2=3
        '1-2': { home: 2, draw: 3, away: 3 },
        // SECONDA - TERZA: 1=2, X=3, 2=3
        '2-3': { home: 2, draw: 3, away: 3 },
        // SECONDA - PRIMA: 1=2, X=3, 2=2
        '2-1': { home: 2, draw: 3, away: 2 },
        // TERZA - SECONDA: 1=2, X=3, 2=2
        '3-2': { home: 2, draw: 3, away: 2 },
    },
    tiers: { autoFromMatchday: 5, topCount: 5, bottomCount: 5 },
    exactResult: { enabled: true, bonus: 3, givesBasePoints: true },
    bonusAllCorrect: { enabled: true, bonus: 2, threshold: 8, bonusAboveThreshold: 3 },
    malusNoneCorrect: { enabled: true, penalty: -1 },
    over: {
        enabled: true,
        penalty: -2,
        levels: [
            { goals: 4, points: 3 },
            { goals: 6, points: 4 },
            { goals: 8, points: 5 },
        ],
        required: false, // decisione utente 2026-08-31: l'OVER è facoltativo
    },
    originality: {
        enabled: true,
        uniqueCorrectBonus: 0.5,
        sharedThreshold: 3,
        sharedPenalty: -0.5,
        excludeDoubles: true,
        excludeBots: true,
        excludeRedColumns: true,
    },
    redColumn: { usePreviousPrediction: true, penalty: -1, applyBonus: true, applyMalus: true },
    topScorer: { enabled: true, totalPrize: 6, tieRule: 'anyCounts' },
    superMatchday: { enabled: false, method: 'manual', multiplier: 2, extraBonus: 0, extraPenalty: 0 },
    tieBreak: { method: 'playoffMatchday' },
    botPlayer: { enabled: true, name: 'Cita' },
});
//# sourceMappingURL=ruleset.js.map